import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  EmployeeHeaderReferenceContractFragment,
  TimesheetMetaEmployeeFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate, i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { convertBackendMinutesToHoursString } from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/utils/extraHoursUtils'
import { useAttendanceContextlessPolicies } from 'modules/attendance/hooks/useAttendanceContextlessPolicies'

interface Props {
  employee: TimesheetMetaEmployeeFragment
  referenceContract?: EmployeeHeaderReferenceContractFragment
  hasTimeTrackingAnnualisationDailyValue: boolean
}

export const TimesheetEmployeeHeader = ({
  employee,
  referenceContract,
  hasTimeTrackingAnnualisationDailyValue,
}: Props) => {
  const avatar = buildUrl(employee.access.avatar)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { accessEditSettings } = useAttendanceContextlessPolicies()
  const recommendedYearlyHours = Math.round(employee.yearlyTimeInfo?.recommendedHours || 0)
  const totalWorkedMinutes = employee.yearlyTimeInfo?.workedMinutes || 0
  const totalWorkedTime = convertBackendMinutesToHoursString(totalWorkedMinutes)
  const referenceDate = new Date()
  referenceDate.setDate(referenceDate.getDate() - 1)

  const headerActions = []

  headerActions.push({
    label: i18n.t('clock_in.header.actions.view_profile'),
    onClick: () => navigate(navigation.employees.id.fullPath({ id: employee.id })),
  })

  const employeesSettingId =
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection.edges?.[0]?.node?.id
  if (accessEditSettings && employeesSettingId) {
    const routeParams = {
      id: 'time_tracking',
      settingId: employeesSettingId,
      tabId: 'edit',
    }

    const route = navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams)

    headerActions.push({
      label: i18n.t('clock_in.header.actions.view_policy_settings'),
      onClick: () => navigate(route),
    })
  }

  const isDaily = referenceContract?.frForfaitJours

  return (
    <Box width="full">
      <ResourceHeader
        avatar={{
          type: 'person',
          firstName: employee.access.firstName,
          lastName: employee.access.lastName || '',
          src: avatar,
        }}
        title={employee.access.fullName}
        description={referenceContract?.jobTitle ?? ''}
        secondaryActions={headerActions}
        metadata={
          isDaily
            ? []
            : [
                !!recommendedYearlyHours &&
                  hasTimeTrackingAnnualisationDailyValue && {
                    label: i18n.t('clock_in.header.metadata.recommended_annual_hours'),
                    value: {
                      type: 'text',
                      content: `${recommendedYearlyHours}${i18n.t(
                        'clock_in.header.metadata.hours_unit'
                      )}`,
                    },
                    info: {
                      title: i18n.t('clock_in.header.metadata.recommended_annual_hours_info_title'),
                      description: i18n.t(
                        'clock_in.header.metadata.recommended_annual_hours_info_description'
                      ),
                    },
                  },
                !!recommendedYearlyHours &&
                  !!totalWorkedTime &&
                  hasTimeTrackingAnnualisationDailyValue && {
                    label: i18n.t('clock_in.header.metadata.total_worked_hours'),
                    value: {
                      type: 'text',
                      content: totalWorkedTime,
                    },
                    info: {
                      title: i18n.t('clock_in.header.metadata.total_worked_hours_info_title'),
                      description: i18n.t(
                        'clock_in.header.metadata.total_worked_hours_info_description',
                        {
                          date: i18nDateToString(referenceDate, 'ui_abbreviated_month_year'),
                          cycle_start: convertDate(
                            employee.yearlyTimeInfo?.legalEntityCycleStartDate ?? '',
                            'backend',
                            'ui_abbreviated_month_year'
                          ),
                        }
                      ),
                    },
                  },
              ]
        }
      />
    </Box>
  )
}
